import { GetPlantOverviewResponse } from '@/features/plants/hooks/queries/[plantId]/use-get-plant-overview.ts';
import style from './plant-production-and-storage-realtime-plant-metrics-display.module.scss';
import { DashboardCard } from '@/components/ui/dashboard-card.tsx';
import { PlantPowerFlow } from '@/assets/plant/power-flow.tsx';
import { useTranslation } from 'react-i18next';
import { cn } from '@/utils/utils.ts';

const formatValue = (value: number | undefined, precision: number = 2) => {
  return value ? +value.toFixed(precision) : 0;
};

const MetricDisplay = ({
  label,
  value,
  unit,
}: {
  label: string;
  value: number;
  unit: string;
}) => {
  return (
    <div className="flex h-min flex-col">
      <span className="text-muted-foreground text-sm font-medium">{label}</span>
      <div className="flex gap-2 text-2xl font-semibold">
        {value}
        <div className="text-muted-foreground text-xl [line-height:1.75]">{unit}</div>
      </div>
    </div>
  );
};

export const PlantProductionAndStorageRealtimePlantMetricsDisplay = ({
  overview,
  isLoading,
  className,
}: {
  overview: GetPlantOverviewResponse | undefined | null;
  isLoading: boolean;
  className?: string;
}) => {
  const { t } = useTranslation();

  const pvProduction = formatValue(overview?.currentYieldPower);
  const consumption = formatValue(overview?.currentConsumptionPower);
  const grid = formatValue(overview?.currentGridPower);
  const batteryPower = formatValue(overview?.currentBatteryPower);
  const batterySoC = overview?.currentBatterySoC ? Math.trunc(overview.currentBatterySoC) : 0;

  const plantStatusMap = {
    1: 'On-grid',
    0: 'Idle',
    unknown: '-',
  };

  const plantStatus: string = overview?.currentPlantStatus
    ? plantStatusMap[overview?.currentPlantStatus]
    : plantStatusMap['unknown'];

  const measurements = {
    production: pvProduction,
    grid: grid,
    consumption: consumption,
    batteryPower: batteryPower,
    batterySoC: batterySoC,
  };

  return (
    <DashboardCard isLoading={isLoading} className={cn(style.overview, className)}>
      <div className={cn(style.overview__container)}>
        <div className="flex min-w-[130px] flex-col gap-4 text-nowrap">
          <h2 className="text-2xl font-semibold text-green-600">{plantStatus}</h2>
          <div className={cn(style.overview__metrics)}>
            <MetricDisplay label={t('metrics.pv_production')} value={pvProduction} unit="kW" />
            <MetricDisplay label={t('metrics.consumption')} value={consumption} unit="kW" />
            <MetricDisplay label={t('metrics.grid')} value={grid} unit="kW" />
            <MetricDisplay
              label={t('metrics.battery_state_of_charge')}
              value={batterySoC}
              unit="%"
            />
          </div>
        </div>
        <div className="flex w-full justify-between">
          <div className="flex h-full w-full items-center justify-center">
            <PlantPowerFlow
              width="100%"
              height="100%"
              className="max-w-[26rem]"
              measurements={measurements}
            />
          </div>
        </div>
      </div>
    </DashboardCard>
  );
};
